import {ActionTree, GetterTree, Module, MutationTree} from "vuex";
import {RootState} from "../store";
import {Difficulty, TypesenseResponse} from "../../../../utils/models/TypesenseResponse";
import {ExploreV2State} from "../../../explore_v2/store/modules/ExploreV2Module";
import {MeasurementPrefUtility} from "../../../../utils/helpers/MeasurementPref";
import graph_client from "../../../../utils/graphql/graph_client";
import {GetParkTrails} from "../../../../utils/graphql/park-trails";

export class ParkTrailsState {

  pageCount: number = 1

  graphResponse: any | null = null
  typesensePageCount: number = 6

  static distanceRangeMin = 0.0
  static distanceRangeMax = 150.0

  selectedDistanceRangeMin: number = 0.0
  selectedDistanceRangeMax: number = 70000

  static elevationRangeMin = 0.0
  static elevationRangeMax = 2500.0

  static durationRangeMin = 0.0
  static durationRangeMax = 250.0

  selectedDurationRangeMin: number = 0.0
  selectedDurationRangeMax: number | null = null

  selectedDifficulties: string[] = []
  selectedTrailTags: string[] = []
  selectedElevations: string[] = []
  selectedMinRating: number | null = null

  searchString: string = ""

}

const actions: ActionTree<ParkTrailsState, RootState> = {
  initialiseStore(store) {
    store.dispatch('getGraphResponse')
  },
  async getGraphResponse(store) {
    graph_client.request(GetParkTrails, {
      park_id: window.parkID,
      max_distance_meters: parseInt(store.state.selectedDistanceRangeMax * 1000),
      difficulties: store.getters.combineAndUniqueifyDifficulties
    }).then((data) => {
      store.commit('setGraphResponse', data)
    })
  }
}

const mutations: MutationTree<ParkTrailsState> = {
  setTypesensePageCount(state, typesensePageCount: number) {
    state.typesensePageCount = typesensePageCount
  },
  clearRating(state) {
    state.selectedMinRating = null
  },
  setRating(state, rating: number) {
    state.selectedMinRating = rating
  },
  removeTrailTag(state, tag: string) {
    state.selectedTrailTags.splice(state.selectedTrailTags.indexOf(tag), 1)
  },
  addTrailTag(state, tag: string) {
    state.selectedTrailTags.push(tag)
  },
  setSelectedTrailTags(state, tags: string[]) {
    state.selectedTrailTags = tags
  },
  setGraphResponse(state, response: any) {
    state.graphResponse = response
  },
  setDistanceRangeMax(state, mxDistance: number) {
    state.selectedDistanceRangeMax = mxDistance
  },
  setDistanceRangeMin(state, mxDistance: number) {
    state.selectedDistanceRangeMin = mxDistance
  },
  restore(state, savedState) {
    Object.assign(state, savedState)
  },
  setDifficulties(state, difficulties: Difficulty[]) {
    state.selectedDifficulties = difficulties
  },
  setElevations(state, elevationDifficulties: Difficulty[]) {
    state.selectedElevations = elevationDifficulties
  },
  incrementPageCount(state) {
    state.pageCount++
  }
}

const getters: GetterTree<ParkTrailsState, RootState> = {
  countToShow(state): number {
    return state.pageCount * 3
  },
  combineAndUniqueifyDifficulties(state) {
    const combinedDifficulties = [...state.selectedDifficulties, ...state.selectedElevations];
    let a = combinedDifficulties.concat();
    for (let i = 0; i < a.length; ++i) {
      for (let j = i + 1; j < a.length; ++j) {
        if (a[i] === a[j])
          a.splice(j--, 1);
      }
    }
    return a;
  },
  hasDistanceFilter(state): boolean {
    return (state.selectedDistanceRangeMax ?? ExploreV2State.distanceRangeMax) < ExploreV2State.distanceRangeMax
  },
  hasDifficultyFilter(state): boolean {
    return state.selectedDifficulties.length != 0
  },
  hasSelectedTags(state) {
    return state.selectedTrailTags.length > 0
  },
  reversedActiveTrails(state) {
    return state.selectedTrailTags.slice().reverse()
  },
  hasElevationsFilter(state): boolean {
    return state.selectedElevations.length != 0
  },
  filterSelectedOptionsMaxDistance(state) {
    let measurementPref = MeasurementPrefUtility.preference()
    // @ts-ignore
    return measurementPref.bigUnitFormatter(state.filterSelectedOptions.selectedDistanceRangeMax).toString()
  },
  hasFilter(state) {
    return state.selectedTrailTags.length > 0 ||
      state.selectedDistanceRangeMax <= 149 ||
      state.selectedElevations.length > 0 ||
      state.selectedDifficulties.length > 0 ||
      state.selectedMinRating != null
  },
}

const state = new ParkTrailsState()

export default class ParkTrailsModule implements Module<ParkTrailsState, RootState> {
  namespaced = true;
  state = state;
  actions = actions;
  mutations = mutations;
  getters = getters;
}
